import { EarningOrderStatus } from 'src/constants/payments-ops/enums'
import type {
  FetchUsBankEarningsPayload,
  UsBankEarningOrder,
} from 'src/interfaces/usBankTransactions'

interface EarningOrdersSummary {
  count: number
  netTotalCents: number
  completedCount: number
}

const encodeText = (value: string | null): string | undefined => {
  const trimmed = value?.trim() ?? ''

  return trimmed === '' ? undefined : encodeURIComponent(trimmed)
}

/**
 * The query for `GET …/us-bank-transactions/earnings`. `paymentProvider` is not sent — the enum
 * has one member (`us_bank`). `createdAtFrom/To` actually filter `uploadedAt`; they are sent as
 * whole UTC days so the "to" day is inclusive.
 */
export const buildUsBankEarningsQuery = (
  payload: FetchUsBankEarningsPayload,
): Record<string, string | number | undefined> => {
  return {
    contractorId: encodeText(payload.contractorId),
    contractorExternalId: encodeText(payload.contractorExternalId),
    partnerDisplayName: encodeText(payload.partnerDisplayName),
    destinationType: payload.destinationType ?? undefined,
    orderStatus: payload.orderStatus ?? undefined,
    createdAtFrom: payload.uploadedFrom ? `${payload.uploadedFrom}T00:00:00.000Z` : undefined,
    createdAtTo: payload.uploadedTo ? `${payload.uploadedTo}T23:59:59.999Z` : undefined,
    activeOnly: payload.activeOnly ? 'true' : undefined,
    limit: payload.limit,
    offset: payload.offset,
  }
}

export const summarizeEarningOrders = (orders: UsBankEarningOrder[]): EarningOrdersSummary => {
  return orders.reduce<EarningOrdersSummary>(
    (summary, order) => {
      return {
        count: summary.count + 1,
        netTotalCents: summary.netTotalCents + (order.orderAmount ?? 0),
        completedCount:
          summary.completedCount + (order.orderStatus === EarningOrderStatus.Completed ? 1 : 0),
      }
    },
    { count: 0, netTotalCents: 0, completedCount: 0 },
  )
}
